// Explore-stats page: Parliamentarian ages over time.
// Two views per chamber — the average age of parliamentarians in each
// Parliament (line, over time) and the age-group breakdown of each Parliament
// (stacked bar). Filter: All / Members (House) / Senators (Senate).

import React, { useState, useEffect } from 'react';
import { Paper, Typography, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatLine, StatStackedBar } from './charts';

export default function ParliamentarianAges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chamber, setChamber] = useState('all'); // 'all' | 'house' | 'senate'

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getExploreStat('parliamentarian-ages',
          chamber === 'all' ? {} : { chamber });
        if (active) { setData(res); setError(null); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [chamber]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Parliamentarian ages over time"
      description="The average age of parliamentarians at the start of each Parliament, the average across everyone who served during it, and how each Parliament breaks down into age groups. Filter by chamber to compare Members (House) and Senators."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="parliamentarian-ages"
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={chamber}
          onChange={(e, v) => v && setChamber(v)}
        >
          <ToggleButton value="all">Both chambers</ToggleButton>
          <ToggleButton value="house">Members (House)</ToggleButton>
          <ToggleButton value="senate">Senators</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Average age at the start of each Parliament
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Parliamentarians serving on the Parliament's opening day, aged as at that date.
            </Typography>
            <StatLine data={charts.opening_age || []} xKey="parliament"
                      keys={charts.opening_age_series || []} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Average age across everyone who served in each Parliament
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Everyone who served at any point, aged as of when their service in that Parliament began (includes members who joined at by-elections or filled casual vacancies).
            </Typography>
            <StatLine data={charts.average_age || []} xKey="parliament"
                      keys={charts.average_age_series || []} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Average age across everyone who served — by year the Parliament started
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The same data as above, plotted against the year each Parliament opened rather than its number.
            </Typography>
            <StatLine data={charts.average_age || []} xKey="year"
                      keys={charts.average_age_series || []} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Age groups in each Parliament
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The share of each Parliament falling in each age band (each column totals 100%).
            </Typography>
            <StatStackedBar data={charts.age_groups || []} xKey="parliament"
                            keys={charts.age_group_series || []} height={420} percent />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
